import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../../services/loginService';
import {
  getFeatureFlags,
  cacheFeatureFlags,
} from '../../services/featureFlagsService';
import { hydrateRiggedCharactersForCurrentUser } from '../../services/riggedCharacterService';
import { fetchAndCacheUserSasFromProfile } from '../../services/characterStorageService';
import LogoIconSrc from './../../assets/icons/logo/pixle-transparent-inverted.png';
import './login.css';
import { useAuth } from '../../App.tsx';
import { toast } from 'react-toastify';
import { identifyClarityUser } from '../../services/clarityService';
import { identifySentryUser } from '../../services/sentryService';
import { authErrorMessage, authMessage } from '../../i18n/authMessages';
import { detectLanguage } from '../../i18n/language';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { setIsAuthenticated } = useAuth(); // Get setIsAuthenticated from context

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const language = detectLanguage();
    setLoading(true);

    try {
      const response = await login({ email, password });

      // Show success toast
      toast.success(authMessage('login.success', language));

      // Store auth token & user data in localStorage
      localStorage.setItem('authToken', response.token);
      const normalizedUser = {
        ...response.user,
        _id: response.user.id,
      };
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      localStorage.setItem('userId', response.user.id);
      await hydrateRiggedCharactersForCurrentUser();

      // Cache the user-level SAS token (returned with the user profile) keyed
      // by the user id so character images can be signed for display.
      await fetchAndCacheUserSasFromProfile(response.token);

      // Link this Clarity session to the logged-in user
      identifyClarityUser(normalizedUser);
      // Link this Sentry context to the logged-in user
      identifySentryUser(normalizedUser);

      // Fetch the user's feature flags so generation flows know which AI model to use
      try {
        const featureFlags = await getFeatureFlags();
        cacheFeatureFlags(featureFlags);
      } catch (error) {
        console.error('Failed to load feature flags after login', error);
      }

      // Redirect to home page
      setTimeout(() => {
        navigate('/');
      }, 2000);

      setIsAuthenticated(true);
    } catch (error: any) {
      toast.error(authErrorMessage(error, 'login.failed', language));
    }

    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="auth-hero">
        <p className="auth-hero-title">
          Pixle — an AI-native animation studio built for creators
        </p>
        <ul className="auth-hero-list">
          <li>Your creative IP remains yours</li>
          <li>Secure payments powered by Stripe</li>
          <li>Access leading AI models in one studio</li>
          <li>Create production-ready animation in minutes</li>
        </ul>
      </div>
      <div className="login-right">
        <img src={LogoIconSrc} alt="Logo Icon" />
        <h2 className="login-title">Ready to create?</h2>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email address *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Type your email address here"
              required
            />
          </div>

          <div className="form-group">
            <label>Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Type your password here"
              required
            />
          </div>

          <div className="forgot-password">
            <div
              onClick={() => navigate('/forgot-password')}
              style={{ cursor: 'pointer' }}
            >
              Forgot password?
            </div>
          </div>

          <div className="login-controls">
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
            <p className="register-text">
              New to Pixle? <a href="/signup">Start your free trial</a>
            </p>
            <p className="legal-footer">
              <a href="/terms">Terms of Service</a> · <a href="/privacy">Privacy Policy</a>
            </p>
          </div>
        </form>
        <p className="login-founder-note">
          Building Pixle with creators.
          <br />
          Have feedback?{' '}
          <a
            href="https://www.linkedin.com/in/priyanka-jhaveri-480bbb27a/"
            target="_blank"
            rel="noreferrer"
          >
            Talk to the founder →
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
